import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Select,
  Typography,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link
} from '@mui/material';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { RouterLinks } from '../../../app-route-paths';
import { enqueueSnackbar, useUserDetailsStore } from '@zeta-core';
import { ComplianceService, getErrorMessage, WPDocumentQA } from '@zeta-services';
import { IGenericErrorResponse } from '@zeta-types';
import { WaitScreenCompliance } from './wait-screen';

// --------------------
// Types
// --------------------
type ComplianceTopic = {
  id: string;
  name: string;
};

// --------------------
// Screen
// --------------------
export default function NewReviewRequestScreen() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedTopicName, setSelectedTopicName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [topics, setTopics] = useState<ComplianceTopic[]>([]);
  const [processing, setProcessing] = useState(false);


  const { id: advisorId } = useUserDetailsStore((s) => s.user);

  // -------------------------------------------------
  // Load compliance topics (unchanged logic)
  // -------------------------------------------------
  const loadTopics = useMutation({
    mutationFn: () =>
      WPDocumentQA().chatSessionsByAdvisor({ advisor_id: advisorId }),

    onSuccess: (res) => {
      const t: ComplianceTopic[] = (res.data || [])
        .filter(
          (s: any) =>
            s.chat_type === 'document_analysis' &&
            s.session_name &&
            s.session_name.toLowerCase() !== 'document assistant'
        )
        .map((s: any) => ({
          id: s.session_id,
          name: s.session_name.trim(),
        }))
        .sort((a: ComplianceTopic, b: ComplianceTopic) =>
          a.name.localeCompare(b.name)
        );

      setTopics(t);
    },
  });

  useEffect(() => {
    loadTopics.mutate();
  }, []);

  // -------------------------------------------------
  // Submit review
  // -------------------------------------------------
  const consumerDutyMutation = useMutation({
    mutationFn: (payload: { file: File; session_id: string; topic: string; advisor_id: string }) =>
      ComplianceService().consumerDuty(payload),

      onSuccess: () => {
        enqueueSnackbar(
          'Document submitted successfully. Zeta AI is processing the review.',
          { variant: 'success' }
        );

        setProcessing(true);

        // ⏳ Give user a moment to read the message, then return to reviews
        setTimeout(() => {
          navigate(RouterLinks.complianceReviews);
        }, 2500);
      },


      onError: (err: IGenericErrorResponse) => {
        const code =
          err?.response?.data?.error?.errorCode ||
          err?.response?.data?.error?.[0]?.errorCode;

        enqueueSnackbar(getErrorMessage(code), { variant: 'error' });
      },
  });

  const canSubmit = useMemo(
    () =>
      !!file &&
      !!selectedTopicId &&
      !consumerDutyMutation.isPending,
    [file, selectedTopicId, consumerDutyMutation.isPending]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file || !selectedTopicId) {
      setError('Please choose a PDF file and compliance topic.');
      return;
    }

    await consumerDutyMutation.mutateAsync({
      file,
      session_id: selectedTopicId,
      topic: selectedTopicName,
      advisor_id: advisorId,
    });
  };

  const handleReset = () => {
    setFile(null);
    setSelectedTopicId('');
    setSelectedTopicName('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // -------------------------------------------------
  // UI
  // -------------------------------------------------
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Breadcrumb */}
      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2, py: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(RouterLinks.complianceReviews)}
          >
            Compliance Reviews
          </Link>
          <Typography color="text.primary">
            New Review
          </Typography>
        </Breadcrumbs>
      </Box>

      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box sx={{ maxWidth: 900, mx: 'auto', mb: 3 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 600 }}
          >
            Zeta Compliance — Compliance Document Review
          </Typography>
        </Box>

        {/* About this Tool (UNCHANGED BUSINESS TEXT) */}
        <Box sx={{ maxWidth: 900, mx: 'auto', mb: 3 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  About this Tool
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <List dense disablePadding>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <UploadFileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Upload a suitability report or compliance document and receive structured compliance feedback based on the selected topic."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <AssessmentIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="View an overall compliance indicator and detailed observations with client and adviser feedback."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LibraryBooksIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="View regulatory and policy references to support internal compliance review."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Upload + Topic Selection (UNCHANGED UI) */}
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <form onSubmit={handleSubmit}>
            {/* Topic */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Compliance topic
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Select the compliance topic this document will be reviewed against.
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Select
                    value={selectedTopicId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedTopicId(id);
                      const t = topics.find((x) => x.id === id);
                      setSelectedTopicName(t?.name ?? '');
                    }}
                    fullWidth
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select a compliance topic
                    </MenuItem>

                    {topics.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {selectedTopicName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Selected topic: {selectedTopicName}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Upload box */}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="application/pdf"
              onChange={(e) =>
                setFile(e.target.files?.[0] || null)
              }
            />

            <Card
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'primary.main',
                cursor: 'pointer',
                textAlign: 'center',
                p: 7,
                mb: 3,
                '&:hover': {
                  borderColor: 'primary.dark',
                },
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Please upload compliance document to review.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag & drop or click to choose (PDF only)
                </Typography>

                {file && (
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    Selected:{' '}
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      {file.name}
                    </Typography>
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Disclaimer (UNCHANGED BUSINESS TEXT) */}
            <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
              <Typography variant="body2">
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  Disclaimer:
                </Typography>{' '}
                Zeta Compliance Agent provides structured compliance feedback and may occasionally produce incomplete or inaccurate outputs.
                Please review outputs carefully and confirm with your firm's compliance department before taking any action.
                Zeta Compliance Agent is not responsible for regulatory outcomes; advisors remain accountable for ensuring compliance with FCA requirements.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Processing typically completes within a few minutes.
                Results will appear automatically in the Compliance Reviews list.
              </Typography>

            </Alert>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit}
                sx={{ borderRadius: 2, px: 4 }}
              >
                {consumerDutyMutation.isPending
                  ? 'Reviewing document…'
                  : 'Run review'}
              </Button>

              <Button
                type="button"
                variant="outlined"
                size="large"
                onClick={handleReset}
                sx={{ borderRadius: 2, px: 4 }}
              >
                Reset
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </form>
        </Box>
        <WaitScreenCompliance
          open={processing || consumerDutyMutation.isPending}
          title="Zeta AI is processing your compliance review…"
        />

      </Container>
    </Box>
  );
}
